import { makeAutoObservable, runInAction, computed } from 'mobx';
import { hsRoomPriorityService } from 'services/HsRoomPriorityService';
import {
  HsRoomPriorityItem,
  RoomFilterData,
  RoomFilterPayload
} from 'Interfaces/HsRoomPriorityInterfaces';

class HsRoomPriorityStore {
  roomPriorities: { [roomCode: string]: HsRoomPriorityItem[] } = {};
  roomFilters: { [roomCode: string]: RoomFilterData } = {};
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this, {
      hasData: computed,
      roomCodes: computed,
      nonEmptyRoomCodes: computed
    });
  }

  fetchRoomPriorities = async (filters?: RoomFilterPayload, setLoadingState: boolean = true) => {
    if (setLoadingState) {
      this.loading = true;
    }
    this.error = null;

    try {
      const data = await hsRoomPriorityService.getHsRoomPriorities(filters);

      runInAction(() => {
        // Update room items from the 'rooms' property
        this.roomPriorities = data.rooms || {};
        // Update filters from metadata
        if (data.metadata && data.metadata.filters) {
          this.roomFilters = data.metadata.filters;
        }
        if (setLoadingState) {
          this.loading = false;
        }
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch room priorities';
        if (setLoadingState) {
          this.loading = false;
        }
      });
    }
  };

  // Get filters for a specific room
  getRoomFilter = (roomCode: string): RoomFilterData | undefined => {
    return this.roomFilters[roomCode];
  };

  // Update filter for a specific room and fetch updated data
  updateRoomFilter = async (roomCode: string, filterData: RoomFilterData) => {
    // Create payload with just the room being filtered
    const payload: RoomFilterPayload = {
      [roomCode]: filterData
    };

    // Fetch with the updated filter
    await this.fetchRoomPriorities(payload, true); // Set loading state for filter updates
  };

  // Get room items by room code
  getRoomItems = (roomCode: string): HsRoomPriorityItem[] => {
    return this.roomPriorities[roomCode] || [];
  };

  get hasData(): boolean {
    return Object.values(this.roomPriorities).some(items => items && items.length > 0);
  }

  get roomCodes(): string[] {
    return Object.keys(this.roomPriorities);
  }

  get nonEmptyRoomCodes(): string[] {
    return Object.entries(this.roomPriorities)
      .filter(([_, items]) => items && items.length > 0)
      .map(([code]) => code);
  }
}

export default HsRoomPriorityStore;
